import type { Hand as PokerHandType } from 'pokersolver';
// eslint-disable-next-line @typescript-eslint/no-require-imports
import * as pokersolverNs from 'pokersolver';

// Pokersolver ships CJS-only and uses `exports.X = X` assignments rather than
// `module.exports = {...}`. Vite/Rollup CJS interop wraps that as a namespace
// object with a `.default` for module.exports AND top-level properties for
// each `exports.X`. Node's ESM-CJS bridge surfaces both forms too. Try
// namespace first, fall back to `.default`.
const solverModule = pokersolverNs as unknown as {
  Hand?: typeof PokerHandType;
  default?: { Hand?: typeof PokerHandType };
};
const PokerHand: typeof PokerHandType = (solverModule.Hand ??
  solverModule.default?.Hand) as typeof PokerHandType;
if (!PokerHand) {
  throw new Error('handEvaluator: pokersolver.Hand not resolvable under current bundler');
}

import type { HandEvaluator, HandRank } from './ports';
import type { Card, PlayerId } from './types';

/**
 * Pokersolver returns cards whose `toString()` replaces 'T' with '10'.
 * Engine-wide card notation is 2-char ('Ts'), so we rebuild from value + suit.
 */
function cardToEngineNotation(c: { value: string; suit: string }): Card {
  return `${c.value}${c.suit}`;
}

/**
 * Spread-copies the input array so pokersolver cannot mutate our state.
 * (Pokersolver internally sorts and mutates its card pool.)
 */
function solveSafe(cards: ReadonlyArray<Card>): PokerHandType {
  return PokerHand.solve([...cards]);
}

function toHandRank(hand: PokerHandType): HandRank {
  return {
    name: hand.name,
    value: hand.rank,
    best: hand.cards.map(cardToEngineNotation),
    description: hand.descr,
  };
}

export function createPokersolverEvaluator(): HandEvaluator {
  return {
    rank(cards) {
      if (cards.length < 5) {
        throw new RangeError(`HandEvaluator.rank requires at least 5 cards, got ${cards.length}`);
      }
      return toHandRank(solveSafe(cards));
    },

    compare(a, b) {
      // Pokersolver Hand.compare inverts the sign convention: negative means
      // the callee is stronger. Re-solve from the best-5 card list and flip.
      const handA = solveSafe(a.best);
      const handB = solveSafe(b.best);
      const raw = handA.compare(handB);
      if (raw < 0) return 1;
      if (raw > 0) return -1;
      return 0;
    },

    winners(contenders, board) {
      if (contenders.length === 0) return [];
      const solved = contenders.map((c) => ({
        playerId: c.playerId,
        hand: solveSafe([...c.holeCards, ...board]),
      }));
      const winningHands = new Set(PokerHand.winners(solved.map((s) => s.hand)));
      const winners: PlayerId[] = [];
      for (const entry of solved) {
        if (winningHands.has(entry.hand)) {
          winners.push(entry.playerId);
        }
      }
      return winners;
    },
  };
}
